import { useState, useEffect, useRef } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';

interface Member {
  id: string;
  name: string;
  [key: string]: any;
}

interface Checklist {
  id: string;
  name: string;
  [key: string]: any;
}

interface Activity {
  id: string;
  name: string;
  icon?: string;
  [key: string]: any;
}

interface FamilyStaticData {
  members: Member[];
  checklists: Checklist[];
  activities: Activity[];
  checklistNameMap: Record<string, string>;
  activityNameMap: Record<string, string>;
  activityIconMap: Record<string, string>;
  loading: boolean;
}

/**
 * 🚀 OPTIMIZACIÓN: Cargar datos estáticos de la familia con UN SOLO LISTENER por colección
 * Reemplaza 6+ listeners duplicados con solo 3 listeners
 */
export function useFamilyStaticData(familyId: string | null): FamilyStaticData {
  const [members, setMembers] = useState<Member[]>([]);
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [checklistNameMap, setChecklistNameMap] = useState<Record<string, string>>({});
  const [activityNameMap, setActivityNameMap] = useState<Record<string, string>>({});
  const [activityIconMap, setActivityIconMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    if (!familyId) {
      setMembers([]);
      setChecklists([]);
      setActivities([]);
      setChecklistNameMap({});
      setActivityNameMap({});
      setActivityIconMap({});
      setLoading(false);
      return;
    }

    setLoading(true);
    let loadedCount = 0;
    const totalListeners = 3;

    const checkLoaded = () => {
      loadedCount++;
      if (loadedCount >= totalListeners && isMountedRef.current) {
        setLoading(false);
      }
    };

    const unsubs: (() => void)[] = [];

    // 1. Listener para Members (reemplaza múltiples listeners duplicados)
    unsubs.push(
      onSnapshot(
        query(collection(db, 'members'), where('familyId', '==', familyId)),
        (snap) => {
          if (!isMountedRef.current) return;
          const raw = snap.docs.map(d => ({ ...(d.data() as any), id: d.id }));
          const byId = new Map<string, Member>();
          raw.forEach((m) => {
            if (!m?.id) return;
            byId.set(String(m.id), m);
          });
          setMembers(Array.from(byId.values()));
          checkLoaded();
        },
        () => {
          setMembers([]);
          checkLoaded();
        }
      )
    );

    // 2. Listener para Checklists (reemplaza múltiples listeners duplicados)
    unsubs.push(
      onSnapshot(
        query(collection(db, 'checklist'), where('familyId', '==', familyId)),
        (snap) => {
          if (!isMountedRef.current) return;
          const raw = snap.docs.map(d => ({ ...(d.data() as any), id: d.id }));
          const byId = new Map<string, Checklist>();
          raw.forEach((c) => {
            if (!c?.id) return;
            byId.set(String(c.id), c);
          });
          const items = Array.from(byId.values());
          setChecklists(items);
          
          // Derivar nameMap directamente
          const nameMap: Record<string, string> = {};
          items.forEach(item => {
            nameMap[item.id] = item.name || item.title || item.summary || item.id;
          });
          setChecklistNameMap(nameMap);
          checkLoaded();
        },
        () => {
          setChecklists([]);
          setChecklistNameMap({});
          checkLoaded();
        }
      )
    );

    // 3. Listener para Activities (reemplaza múltiples listeners duplicados)
    unsubs.push(
      onSnapshot(
        query(collection(db, 'activities'), where('familyId', '==', familyId)),
        (snap) => {
          if (!isMountedRef.current) return;
          const raw = snap.docs.map(d => ({ ...(d.data() as any), id: d.id }));
          const byId = new Map<string, Activity>();
          raw.forEach((a) => {
            if (!a?.id) return;
            byId.set(String(a.id), a);
          });
          const items = Array.from(byId.values());
          setActivities(items);
          
          // Derivar name/icon maps directamente
          const nameMap: Record<string, string> = {};
          const iconMap: Record<string, string> = {};
          items.forEach(item => {
            nameMap[item.id] = item.name || item.title || item.summary || item.id;
            iconMap[item.id] = item.icon || '⚡';
          });
          setActivityNameMap(nameMap);
          setActivityIconMap(iconMap);
          checkLoaded();
        },
        () => {
          setActivities([]);
          setActivityNameMap({});
          setActivityIconMap({});
          checkLoaded();
        }
      )
    );

    return () => {
      isMountedRef.current = false;
      unsubs.forEach(fn => {
        try {
          fn();
        } catch (e) {
          // Ignore errors when listener is already closed
        }
      });
    };
  }, [familyId]);

  return {
    members,
    checklists,
    activities,
    checklistNameMap,
    activityNameMap,
    activityIconMap,
    loading
  };
}

export default useFamilyStaticData;
